// src/components/SensorDataBox.js

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { getSensations } from '../lib/assets.js';

function averageReading(sensations) {
  if (sensations.length) {
    let sum = 0;
    sensations.forEach(s => sum += parseFloat(s.state));
    return sum / sensations.length;
  }

  return null;
}

function detectMotion(sensations) {
  return sensations.some(s => parseInt(s.state) === 1 || s.state === "ON");
}

function SensorDataBox({ sensors, tokenData, prevTokenData, detectionMessages }) {
  const [temperatures, setTemperatures] = useState({});
  const [lights, setLights] = useState({});
  const [motions, setMotions] = useState({});
  const [humidity, setHumidity] = useState({});
  const [loading, setLoading] = useState(false);

  const roomIcons = {
    kitchen: 'kitchen',
    livingRoom: 'weekend',
    bedroom: 'bed',
    bathroom: 'bathtub',
  };

  useEffect(() => {
    if (!tokenData || !sensors || !tokenData.timestamp) {
      setTemperatures({});
      setLights({});
      setMotions({});
      setHumidity({});
      return;
    }

    let cancelled = false;

    async function loadSensorData() {
      setLoading(true);

      const newTemps = {};
      const newLights = {};
      const newMotions = {};
      const newHumidity = {};

      const timeStart = dayjs(tokenData.timestamp)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');

      const timeEnd = dayjs(tokenData.timestamp)
        .add(15, 'minutes')
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');

      await Promise.all(
        Object.keys(sensors).map(async loc => {
          const sensor = sensors[loc];

          if (!sensor || !tokenData.start_at) {
            return;
          }

          if (sensor.temperature) {
            const rsp = await getSensations({
              dtf: {
                item_id: sensor.temperature,
                created_at: ['><', timeStart, timeEnd],
              },
            });

            newTemps[loc] = averageReading(rsp);
          }

          if (sensor.light) {
            const rsp = await getSensations({
              dtf: {
                item_id: sensor.light,
                created_at: ['><', timeStart, timeEnd],
              },
            });

            newLights[loc] = averageReading(rsp);
          }

          if (sensor.motion) {
            const rsp = await getSensations({
              dtf: {
                item_id: sensor.motion,
                created_at: ['><', timeStart, timeEnd],
              },
            });

            newMotions[loc] = detectMotion(rsp);
          }

          if (sensor.humidity) {
            const rsp = await getSensations({
              dtf: {
                item_id: sensor.humidity,
                created_at: ['><', timeStart, timeEnd],
              },
            });

            newHumidity[loc] = averageReading(rsp);
          }
        })
      );

      if (!cancelled) {
        setTemperatures(newTemps);
        setLights(newLights);
        setMotions(newMotions);
        setHumidity(newHumidity);
        setLoading(false);
      }
    }

    loadSensorData();

    return () => {
      cancelled = true;
    };
  }, [sensors, tokenData]);

  if (!tokenData) {
    return (
      <div className="sensor-panel empty">
        <p>Click a time token to view sensor data</p>
      </div>
    );
  }

  const tempDiff = prevTokenData
    ? tokenData.kitchen.temperature - prevTokenData.kitchen.temperature
    : 0;

  const humidityDiff = prevTokenData
    ? tokenData.bathroom.humidity - prevTokenData.bathroom.humidity
    : 0;

  const lightDiff = prevTokenData
    ? tokenData.kitchen.light - prevTokenData.kitchen.light
    : 0;

  const hasTempChange = Math.abs(tempDiff) >= 4.0;
  const hasHumChange = Math.abs(humidityDiff) >= 2.0;
  const hasLightChange = Math.abs(lightDiff) >= 20;

  const formatChange = diff =>
    diff === 0 ? '' : ` (${diff > 0 ? '+' : ''}${diff.toFixed(1)}°C change)`;

  const formatHumChange = diff =>
    diff === 0 ? '' : ` (${diff > 0 ? '+' : ''}${diff.toFixed(1)}% change)`;

  const formatLightChange = diff =>
    diff === 0 ? '' : ` (${diff > 0 ? '+' : ''}${diff.toFixed(0)}% change)`;

  return (
    <div className="sensor-panel">
      <h3>Sensor Data</h3>

      {loading && (
        <div className="sensor-loading">
          Loading sensor readings...
        </div>
      )}

      {detectionMessages?.motionMessage && (
        <div style={{
          background: '#e3f2fd',
          color: '#1976d2',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          border: '2px solid #90caf9',
          fontSize: '1.15em'
        }}>
          {detectionMessages.motionMessage}
        </div>
      )}

      {hasTempChange && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          border: '2px solid #ef9a9a',
          fontSize: '1.15em'
        }}>
          Significant temperature change {tempDiff > 0 ? '↑' : '↓'} ({Math.abs(tempDiff).toFixed(1)}°C)
        </div>
      )}

      {hasHumChange && (
        <div style={{
          background: '#e8f5e9',
          color: '#2e7d32',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          border: '2px solid #a5d6a7',
          fontSize: '1.15em'
        }}>
          Significant humidity change {humidityDiff > 0 ? '↑' : '↓'} ({Math.abs(humidityDiff).toFixed(1)}%)
        </div>
      )}

      {hasLightChange && (
        <div style={{
          background: '#fff8e1',
          color: '#ff8f00',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontWeight: 'bold',
          border: '2px solid #ffcc80',
          fontSize: '1.15em'
        }}>
          Significant light change {lightDiff > 0 ? '↑' : '↓'} ({Math.abs(lightDiff).toFixed(0)}%)
        </div>
      )}

      <div className="sensor-grid">
        {['kitchen', 'livingRoom', 'bedroom', 'bathroom'].map(room => {
          const data = tokenData[room] || {};
          const icon = roomIcons[room];

          /*const tempValue = temperatures[room] ?? data.temperature;
          const lightValue = lights[room] ?? data.light;
          const motionValue = motions[room] ?? data.motion;
          const humidityValue = humidity[room] ?? data.humidity;*/
          const tempValue = temperatures[room] ?? '-';
          const lightValue = lights[room] ?? '-';
          const motionValue = motions[room] ?? '-';
          const humidityValue = humidity[room] ?? '-';

          return (
            <div key={room} className="sensor-card">
              <h3>
                <span
                  className="material-icons"
                  style={{
                    verticalAlign: 'middle',
                    marginRight: '8px',
                    fontSize: '24px'
                  }}
                >
                  {icon}
                </span>
                {room.charAt(0).toUpperCase() + room.slice(1).replace(/([A-Z])/g, ' $1')}
              </h3>

              <p>
                <strong>Temp:</strong>{' '}
                <span style={{
                  color: hasTempChange ? (tempDiff > 0 ? '#c62828' : '#2e7d32') : 'inherit',
                  fontWeight: hasTempChange ? 'bold' : 'normal'
                }}>
                  {tempValue != null ? `${tempValue.toFixed?.(1) ?? tempValue}°C` : '—'}
                  {tempDiff !== 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '0.95em' }}>
                      {formatChange(tempDiff)}
                    </span>
                  )}
                </span>
              </p>

              <p>
                <strong>Light:</strong>{' '}
                <span style={{
                  color: hasLightChange ? (lightDiff > 0 ? '#ff8f00' : '#6d4c41') : 'inherit',
                  fontWeight: hasLightChange ? 'bold' : 'normal'
                }}>
                  {lightValue != null ? `${lightValue.toFixed?.(0) ?? lightValue}%` : '—'}
                  {lightDiff !== 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '0.95em' }}>
                      {formatLightChange(lightDiff)}
                    </span>
                  )}
                </span>
              </p>

              <p>
                <strong>Motion:</strong>{' '}
                {typeof motionValue === 'boolean'
                  ? motionValue ? 'Detected' : 'None'
                  : motionValue ?? '—'}
              </p>

              {room === 'bathroom' && (
                <p>
                  <strong>Humidity:</strong>{' '}
                  <span style={{
                    color: hasHumChange ? (humidityDiff > 0 ? '#2e7d32' : '#ef6c00') : 'inherit',
                    fontWeight: hasHumChange ? 'bold' : 'normal'
                  }}>
                    {humidityValue != null ? `${humidityValue.toFixed?.(1) ?? humidityValue}%` : '—'}
                    {humidityDiff !== 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '0.95em' }}>
                        {formatHumChange(humidityDiff)}
                      </span>
                    )}
                  </span>
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SensorDataBox;